import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { getDateToDisplay } from "../../../../utils/nutrition-helpers";

const CopyMealPlanToPlanSelector = ({
  selectedWeeksByTemplate,
  selectedCurrentTemplateWeeks,
  assignedMealPlans,
  selectedAthlete,
  setState,
}) => {
  const filteredMealPlans = useMemo(() => {
    return selectedAthlete
      ? assignedMealPlans.filter(
          ({ athleteId }) => athleteId === selectedAthlete
        )
      : [];
  }, [assignedMealPlans, selectedAthlete]);

  /**
   * Function to select the weeks
   * @param {*} templateId
   * @param {*} totalWeeks
   * @param {*} weekIndex
   */
  const handleSelectionChangeForToWeeks = (
    templateId,
    totalWeeks = 0,
    weekIndex
  ) => {
    setState((prevState) => {
      const currentWeeks = prevState.selectedWeeksByTemplate[templateId] || [];

      // If we click the selected week, we are going to clear the selection
      if (currentWeeks[0] === weekIndex) {
        return {
          ...prevState,
          selectedWeeksByTemplate: {
            ...prevState.selectedWeeksByTemplate,
            [templateId]: [],
          },
        };
      }

      const maxWeeksToSelect = Math.min(
        selectedCurrentTemplateWeeks.length,
        totalWeeks - weekIndex
      );
      const newWeeks = Array.from(
        { length: maxWeeksToSelect },
        (_, i) => weekIndex + i
      );

      return {
        ...prevState,
        selectedWeeksByTemplate: {
          ...prevState.selectedWeeksByTemplate,
          [templateId]: newWeeks,
        },
      };
    });
  };

  const { t } = useTranslation("coaching-portal");
  return (
    <div className="templates-container">
      <div>
        <div className="header">
          <span className="title">{t("allTemplates.copyMealPlanTo.nutritionPlan")}</span>
          <span className="subtitle">
            <span className="opacity-70">
              {t("allTemplates.copyMealPlanTo.selectWhereToCopy")}
            </span>{" "}
            {selectedCurrentTemplateWeeks.length}{" "}
            {selectedCurrentTemplateWeeks.length === 1 ? t("allTemplates.copyMealPlanTo.weekCount") : t("allTemplates.copyMealPlanTo.weeks")}
          </span>
        </div>
        <div className="templates">
          {filteredMealPlans?.map(
            ({ _id, weeks, utcStartDate, utcEndDate, macrosTotal }) => {
              const {
                calories = 0,
                proteins = 0,
                carbs = 0,
                fats = 0,
              } = macrosTotal;
              const selectedWeeks = selectedWeeksByTemplate[_id];
              return (
                <div className="main-template" key={_id}>
                  <div className="template">
                    <div className="header">
                      <span>
                        {getDateToDisplay(utcStartDate)} -{" "}
                        {getDateToDisplay(utcEndDate)}
                      </span>
                      {selectedWeeks &&
                        selectedWeeks.length > 0 &&
                        selectedCurrentTemplateWeeks.length >
                          selectedWeeks.length && (
                          <span className="chip warning length-div">
                            {t("allTemplates.copyMealPlanTo.onlyWeeksCanBeCopied", {
                              selected: selectedWeeks.length,
                              total: selectedCurrentTemplateWeeks?.length,
                            })}
                          </span>
                        )}

                      <span className="nutrition">
                        kCal {calories} | P {proteins}g | C {carbs}g | F {fats}g
                      </span>
                    </div>
                    <div className="content">
                      <ul>
                        {weeks.map((week, weekIndex) => (
                          <li key={`${_id}-${weekIndex}`}>
                            <SvgIcon
                              onClick={() =>
                                handleSelectionChangeForToWeeks(
                                  _id,
                                  weeks.length,
                                  weekIndex
                                )
                              }
                              src={`/img/new-icons/${
                                selectedWeeksByTemplate[_id]?.includes(
                                  weekIndex
                                )
                                  ? "checked-square.svg"
                                  : "unchecked-square.svg"
                              }`}
                              noFilterOnLight={selectedWeeksByTemplate[
                                _id
                              ]?.includes(weekIndex)}
                            />
                            <span>{t("allTemplates.copyMealPlanTo.week")} {weekIndex + 1}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

CopyMealPlanToPlanSelector.propTypes = {
  selectedWeeksByTemplate: PropTypes.object,
  selectedCurrentTemplateWeeks: PropTypes.array,
  assignedMealPlans: PropTypes.array,
  selectedAthlete: PropTypes.string,
  setState: PropTypes.func,
};

export default CopyMealPlanToPlanSelector;
